import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Plus, X, Pencil } from "lucide-react";
import { toast } from "sonner";

import { API } from "../../lib/backend";

const CATEGORIES = [
  "Certificazioni",
  "Consegna",
  "Pagamento",
  "Contatti",
  "Blocchi/restrizioni",
  "Altro",
];

const CAT_COLOR = {
  Certificazioni: { bg: "#E0F2FE", fg: "#075985" },
  Consegna: { bg: "#F3E8FF", fg: "#6B21A8" },
  Pagamento: { bg: "#DCFCE7", fg: "#166534" },
  Contatti: { bg: "#FEF3C7", fg: "#92400E" },
  "Blocchi/restrizioni": { bg: "#FEE2E2", fg: "#991B1B" },
  Altro: { bg: "#E5E7EB", fg: "#374151" },
};

/**
 * FIX G — Note cliente apprendibili. 2 parti:
 * 1. Textarea libera auto-save (debounced 2s) persisted in customers.free_notes
 * 2. Lista preferenze strutturate per categoria, CRUD inline.
 *
 * Se ``emailDomain`` è falsy (es. offerta senza cliente riconosciuto) il
 * componente renderizza un placeholder.
 */
export default function NoteClienteRiquadro({ emailDomain }) {
  const [freeNotes, setFreeNotes] = useState("");
  const [prefs, setPrefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    categoria: "Certificazioni",
    chiave: "",
    valore: "",
  });
  const debounceRef = useRef(null);
  const lastSavedRef = useRef("");

  // Initial load whenever the customer changes.
  useEffect(() => {
    if (!emailDomain) {
      setFreeNotes("");
      setPrefs([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/customers/${encodeURIComponent(emailDomain)}/preferences`,
          { withCredentials: true }
        );
        if (cancelled) return;
        setFreeNotes(r.data.free_notes || "");
        lastSavedRef.current = r.data.free_notes || "";
        setPrefs(r.data.structured_preferences || []);
      } catch {
        /* silent — 404 means no customer doc yet, which is fine */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [emailDomain]);

  // Debounced auto-save for free_notes (2s idle).
  useEffect(() => {
    if (!emailDomain) return;
    if (freeNotes === lastSavedRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await axios.patch(
          `${API}/customers/${encodeURIComponent(emailDomain)}/free-notes`,
          { notes: freeNotes },
          { withCredentials: true }
        );
        lastSavedRef.current = freeNotes;
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore salvataggio note");
      }
    }, 2000);
    return () => clearTimeout(debounceRef.current);
  }, [freeNotes, emailDomain]);

  const addPref = async () => {
    if (!emailDomain) return;
    if (!form.chiave.trim()) {
      toast.error("La chiave è obbligatoria");
      return;
    }
    try {
      const r = await axios.post(
        `${API}/customers/${encodeURIComponent(emailDomain)}/preferences`,
        form,
        { withCredentials: true }
      );
      setPrefs((p) => [r.data, ...p]);
      setForm({ categoria: "Certificazioni", chiave: "", valore: "" });
      setShowAdd(false);
      toast.success("Preferenza aggiunta");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiunta");
    }
  };

  const deletePref = async (id) => {
    if (!emailDomain) return;
    try {
      await axios.delete(
        `${API}/customers/${encodeURIComponent(emailDomain)}/preferences/${id}`,
        { withCredentials: true }
      );
      setPrefs((p) => p.filter((x) => x.id !== id));
      toast.success("Preferenza rimossa");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rimozione");
    }
  };

  if (!emailDomain) {
    return (
      <div
        className="py-3 text-[11px]"
        style={{ color: "var(--text-muted)" }}
        data-testid="note-cliente-empty"
      >
        Le note cliente saranno disponibili una volta identificato il cliente
        dalla richiesta.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="note-cliente-panel">
      <div>
        <label
          className="text-[11px] font-semibold uppercase tracking-wide block mb-1"
          style={{ color: "var(--text-muted)" }}
        >
          Note libere su questo cliente
        </label>
        <textarea
          value={freeNotes}
          onChange={(e) => setFreeNotes(e.target.value)}
          rows={4}
          maxLength={2000}
          placeholder="Es. lavora solo su ordini >500€ · preferenze specifiche · contatti utili…"
          className="w-full text-[12px] p-2 rounded resize-none"
          style={{
            border: "1px solid var(--border-subtle)",
            outline: "none",
            color: "var(--text-primary)",
            background: "var(--bg-surface)",
          }}
          data-testid="free-notes-textarea"
          disabled={loading}
        />
        <div
          className="text-[10px] mt-0.5 flex items-center justify-between"
          style={{ color: "var(--text-muted)" }}
        >
          <span>
            {freeNotes === lastSavedRef.current ? "✓ Salvato" : "Salvataggio…"}
          </span>
          <span>{freeNotes.length}/2000</span>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label
            className="text-[11px] font-semibold uppercase tracking-wide"
            style={{ color: "var(--text-muted)" }}
          >
            Preferenze strutturate
          </label>
          <button
            type="button"
            onClick={() => setShowAdd((s) => !s)}
            className="inline-flex items-center gap-1 text-[11px] font-medium"
            style={{ color: "var(--accent-primary)" }}
            data-testid="add-pref-btn"
          >
            <Plus className="h-3 w-3" />
            {showAdd ? "Annulla" : "Aggiungi"}
          </button>
        </div>

        {showAdd && (
          <div
            className="p-2 mb-2 rounded space-y-2"
            style={{
              border: "1px solid var(--border-subtle)",
              background: "var(--bg-subtle)",
            }}
            data-testid="add-pref-form"
          >
            <select
              value={form.categoria}
              onChange={(e) => setForm({ ...form, categoria: e.target.value })}
              className="w-full text-[12px] h-7 px-2 rounded"
              style={{
                border: "1px solid var(--border-subtle)",
                background: "white",
              }}
              data-testid="add-pref-categoria"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={form.chiave}
              placeholder="Chiave (es. certificato EN 10204)"
              onChange={(e) => setForm({ ...form, chiave: e.target.value })}
              className="w-full text-[12px] h-7 px-2 rounded"
              style={{
                border: "1px solid var(--border-subtle)",
                background: "white",
              }}
              data-testid="add-pref-chiave"
            />
            <input
              type="text"
              value={form.valore}
              placeholder="Valore (es. sempre 3.1)"
              onChange={(e) => setForm({ ...form, valore: e.target.value })}
              className="w-full text-[12px] h-7 px-2 rounded"
              style={{
                border: "1px solid var(--border-subtle)",
                background: "white",
              }}
              data-testid="add-pref-valore"
            />
            <button
              type="button"
              onClick={addPref}
              className="w-full text-[11px] h-7 rounded font-medium text-white"
              style={{ background: "var(--accent-primary)" }}
              data-testid="add-pref-save"
            >
              Salva preferenza
            </button>
          </div>
        )}

        {prefs.length === 0 && !showAdd && (
          <div
            className="text-[11px] italic py-1"
            style={{ color: "var(--text-muted)" }}
          >
            Nessuna preferenza salvata per questo cliente.
          </div>
        )}

        <ul className="space-y-1.5" data-testid="prefs-list">
          {prefs.map((p) => {
            const c = CAT_COLOR[p.categoria] || CAT_COLOR.Altro;
            return (
              <li
                key={p.id}
                className="flex items-start gap-2 p-1.5 rounded"
                style={{ border: "1px solid var(--border-subtle)" }}
                data-testid={`pref-item-${p.id}`}
              >
                <span
                  className="inline-block shrink-0 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide"
                  style={{ background: c.bg, color: c.fg }}
                >
                  {p.categoria}
                </span>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-[12px] font-medium truncate"
                    style={{ color: "var(--text-primary)" }}
                    title={p.chiave}
                  >
                    {p.chiave}
                  </div>
                  {p.valore && (
                    <div
                      className="text-[11px] truncate"
                      style={{ color: "var(--text-muted)" }}
                      title={p.valore}
                    >
                      {p.valore}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => deletePref(p.id)}
                  className="shrink-0 text-[11px]"
                  style={{ color: "var(--text-muted)" }}
                  title="Rimuovi"
                  data-testid={`pref-delete-${p.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
